import type { ColumnDef } from '@tanstack/react-table'
import {
  BookOpen,
  Calendar,
  Check,
  Code,
  Crown,
  ExternalLink,
  Library,
  Plus,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import {
  copyWithToast,
  type DeferredRowClicks,
  useDeferredRowClicks,
} from '@/components/admin/shared/hooks/use-deferred-row-clicks'
import { SourceIconCell } from '@/components/admin/shared/source-icon-cell'
import { SourceIconStack } from '@/components/admin/shared/source-icon-stack'
import {
  compareByFilterMatchCount,
  createDeleteColumn,
  DifficultyBadge,
} from '@/components/admin/shared/table-columns'
import type { ProblemType } from '../../../../../db/schema/enums.js'
import { Badge } from '../../../shared/badge'
import type { DifficultyInfo, StudyPlanSummary } from '../../types'
import type { CoachingQuestion, CoachingQuestionHistory } from '../types'

interface UseColumnDefinitionsOptions {
  questionHistoryMap: Map<string, CoachingQuestionHistory>
  assignedHomeworkSet: Set<string>
  onDeleteQuestion: (questionId: string) => void
  onAssignHomework?: (questionId: string) => Promise<void>
  /** Whether we're in client context (affects column order) */
  isClientContext: boolean
  /** Current tags filter for custom sorting */
  tagsFilter: string[]
}

/**
 * Custom hook for creating column definitions for the MasterQuestionsTable.
 * Encapsulates all column rendering logic.
 *
 * In client context, addHomework and doneBefore columns appear first.
 * In non-client context, they appear last (and are typically hidden).
 */
export function useColumnDefinitions({
  questionHistoryMap,
  assignedHomeworkSet,
  onDeleteQuestion,
  onAssignHomework,
  isClientContext,
  tagsFilter,
}: UseColumnDefinitionsOptions): ColumnDef<CoachingQuestion>[] {
  const clicks = useDeferredRowClicks()

  return useMemo<ColumnDef<CoachingQuestion>[]>(() => {
    // Client-specific columns
    const clientColumns = [
      createAddHomeworkColumn(assignedHomeworkSet, onAssignHomework),
      createDoneBeforeColumn(questionHistoryMap),
    ]

    // Standard columns
    const standardColumns = [
      createExternalIdColumn(),
      createNameColumn(clicks),
      createDifficultyColumn(),
      createPremiumColumn(),
      createTagsColumn(tagsFilter),
      createAcceptanceRateColumn(),
      createSourceColumn(),
      createStudyPlansColumn(),
      createEditorialColumn(),
      createDeleteColumn<CoachingQuestion>({
        getName: (question) => question.name,
        onDelete: onDeleteQuestion,
        title: 'Delete question',
      }),
    ]

    // In client context, put client columns first; otherwise put them last
    return isClientContext
      ? [...clientColumns, ...standardColumns]
      : [...standardColumns, ...clientColumns]
  }, [
    questionHistoryMap,
    assignedHomeworkSet,
    onDeleteQuestion,
    onAssignHomework,
    isClientContext,
    tagsFilter,
    clicks,
  ])
}

// =============================================================================
// Column Factory Functions
// =============================================================================

function AddHomeworkCell({
  questionId,
  questionName,
  isAssigned,
  onAssignHomework,
}: {
  questionId: string
  questionName: string
  isAssigned: boolean
  onAssignHomework?: (questionId: string) => Promise<void>
}) {
  const [isLoading, setIsLoading] = useState(false)

  const handleAssign = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onAssignHomework || isAssigned || isLoading) return
    setIsLoading(true)
    try {
      await onAssignHomework(questionId)
      toast.success(`Added "${questionName}" to homework`)
    } catch {
      toast.error('Failed to add homework')
    } finally {
      setIsLoading(false)
    }
  }

  if (isAssigned) {
    return (
      <span
        className="flex items-center justify-center text-emerald-500"
        title="Already assigned"
      >
        <Check size={16} />
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={handleAssign}
      disabled={isLoading}
      className="hover:bg-primary/10 text-primary hover:text-primary flex items-center justify-center rounded p-1.5 transition-colors disabled:opacity-50"
      title="Add to homework"
    >
      {isLoading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <Plus size={16} />
      )}
    </button>
  )
}

function createAddHomeworkColumn(
  assignedHomeworkSet: Set<string>,
  onAssignHomework?: (questionId: string) => Promise<void>,
): ColumnDef<CoachingQuestion> {
  return {
    id: 'addHomework',
    header: '+',
    size: 45,
    enableSorting: false,
    cell: ({ row }) => {
      const isAssigned = assignedHomeworkSet.has(row.original.id)
      return (
        <AddHomeworkCell
          questionId={row.original.id}
          questionName={row.original.name}
          isAssigned={isAssigned}
          onAssignHomework={onAssignHomework}
        />
      )
    },
  }
}

function createDoneBeforeColumn(
  questionHistoryMap: Map<string, CoachingQuestionHistory>,
): ColumnDef<CoachingQuestion> {
  return {
    id: 'doneBefore',
    header: 'Done',
    size: 70,
    enableSorting: true,
    accessorFn: (row) => {
      const history = questionHistoryMap.get(row.id)
      if (!history?.doneBefore) return null
      return history.lastAttemptDate
    },
    cell: ({ row }) => {
      const history = questionHistoryMap.get(row.original.id)
      if (!history?.doneBefore) {
        return <span className="text-muted-foreground/50 text-xs">—</span>
      }
      const lastAttempt = history.lastAttemptDate
      if (!lastAttempt) {
        return (
          <span className="flex items-center gap-1 text-emerald-500">
            <Check size={14} />
            <span className="text-xs">Yes</span>
          </span>
        )
      }
      const date = new Date(lastAttempt)
      const now = new Date()
      const isCurrentYear = date.getFullYear() === now.getFullYear()
      const formatted = isCurrentYear
        ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: '2-digit',
          })
      return (
        <span
          className="flex items-center gap-1 text-emerald-500"
          title={date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        >
          <Calendar size={12} />
          <span className="text-xs">{formatted}</span>
        </span>
      )
    },
  }
}

function createExternalIdColumn(): ColumnDef<CoachingQuestion> {
  return {
    id: 'externalId',
    header: 'External ID',
    accessorKey: 'externalId',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue() as string | null
      return value ? (
        <span className="text-muted-foreground font-mono text-xs">{value}</span>
      ) : (
        <span className="text-muted-foreground/50 text-xs">—</span>
      )
    },
  }
}

function createNameColumn(
  clicks: DeferredRowClicks,
): ColumnDef<CoachingQuestion> {
  return {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    size: 280,
    cell: ({ row, getValue }) => {
      const url = row.original.url
      const allSources = row.original.allSources
      const sourceIcon = row.original.sourceIcon
      const sourceName = row.original.source
      const name = getValue() as string
      const questionId = row.original.id

      const handleCopyName = (e: React.MouseEvent) => {
        e.stopPropagation()
        clicks.onSingleClick(questionId, () => {
          copyWithToast(
            name,
            `Copied "${name}" to clipboard`,
            'Failed to copy to clipboard',
          )
        })
      }

      const handleCopyUrl = (e: React.MouseEvent) => {
        e.stopPropagation()
        clicks.onDoubleClick(questionId, () => {
          if (!url) return
          copyWithToast(
            url,
            'Copied problem URL to clipboard',
            'Failed to copy URL to clipboard',
          )
        })
      }

      const handleOpenUrl = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer')
        }
      }

      // Check if sources have different titles
      const hasMultipleNames =
        allSources &&
        allSources.length > 1 &&
        allSources.some((s) => s.title && s.title !== name)

      // When sources have different names, show each icon paired with its name
      if (hasMultipleNames && allSources) {
        return (
          <div className="flex items-center gap-2 text-sm">
            {allSources.map((s, i) => {
              return (
                <span
                  key={s.id}
                  className="flex items-center gap-1"
                  title={s.source}
                >
                  {i > 0 && (
                    <span className="text-muted-foreground mr-1">◆</span>
                  )}
                  <AuthoredIcon
                    icon={s.sourceIcon}
                    className="text-muted-foreground shrink-0 rounded"
                    fallback={Code}
                  />
                  <span
                    className={`truncate ${url ? 'text-primary' : 'text-foreground'}`}
                  >
                    {s.title || name}
                  </span>
                </span>
              )
            })}
            {url && (
              <button
                type="button"
                onClick={handleOpenUrl}
                className="text-primary shrink-0 transition-opacity hover:opacity-70"
                title={`Open on ${sourceName || 'external site'}`}
              >
                <ExternalLink size={12} />
              </button>
            )}
          </div>
        )
      }

      // Single name: show all source icons together, then the name.
      const iconElement = (
        <SourceIconStack
          sources={allSources}
          fallbackIcon={sourceIcon}
          fallback={Code}
        />
      )

      return (
        <div className="flex items-center gap-2 text-sm">
          {url ? (
            <button
              type="button"
              onClick={handleOpenUrl}
              className="shrink-0 transition-opacity hover:opacity-70"
              title={`Open on ${sourceName || 'external site'}`}
            >
              {iconElement}
            </button>
          ) : (
            <span className="shrink-0">{iconElement}</span>
          )}
          <button
            type="button"
            onClick={handleCopyName}
            onDoubleClick={handleCopyUrl}
            className={`truncate text-left hover:underline ${url ? 'text-primary' : 'text-foreground'}`}
            title="Click to copy problem name, double-click to copy URL"
          >
            {name}
          </button>
          {url && (
            <button
              type="button"
              onClick={handleOpenUrl}
              className="text-primary shrink-0 transition-opacity hover:opacity-70"
              title={`Open on ${sourceName || 'external site'}`}
            >
              <ExternalLink size={12} />
            </button>
          )}
        </div>
      )
    },
  }
}

function createDifficultyColumn(): ColumnDef<CoachingQuestion> {
  return {
    id: 'difficulty',
    header: 'Difficulty',
    accessorKey: 'difficulty',
    size: 100,
    cell: ({ getValue }) => (
      <DifficultyBadge
        difficulty={getValue() as DifficultyInfo | null | undefined}
      />
    ),
  }
}

function createPremiumColumn(): ColumnDef<CoachingQuestion> {
  return {
    id: 'premiumRequired',
    header: 'Premium',
    accessorKey: 'premiumRequired',
    size: 80,
    cell: ({ getValue }) =>
      getValue() ? (
        <span title="Premium required">
          <Crown size={14} className="text-amber-400" />
        </span>
      ) : null,
  }
}

/** Human-readable labels for the coding-surface axis (Problem.type). */
const PROBLEM_TYPE_LABELS: Record<ProblemType, string> = {
  algorithms: 'Algorithms',
  sql: 'SQL',
  shell: 'Shell',
}

function createTagsColumn(
  tagsFilter: string[] = [],
): ColumnDef<CoachingQuestion> {
  return {
    id: 'tags',
    header: 'Tags',
    accessorFn: (row) => row.problemTags,
    size: 200,
    enableSorting: true,
    sortingFn: (rowA, rowB) =>
      compareByFilterMatchCount(
        (rowA.original.problemTags || []).map((tag) => tag.name),
        (rowB.original.problemTags || []).map((tag) => tag.name),
        tagsFilter,
      ),
    cell: ({ row }) => {
      const type = row.original.type
      const tags = row.original.problemTags || []
      return (
        <div className="flex flex-wrap gap-1">
          {type !== 'algorithms' && (
            <Badge variant="default" className="text-[10px]">
              {PROBLEM_TYPE_LABELS[type]}
            </Badge>
          )}
          {tags.slice(0, 3).map((tag) => (
            <Badge key={tag.slug} variant="topic" className="text-[10px]">
              {tag.name}
            </Badge>
          ))}
          {tags.length > 3 && (
            <span className="text-muted-foreground text-xs">
              +{tags.length - 3}
            </span>
          )}
        </div>
      )
    },
  }
}

function createAcceptanceRateColumn(): ColumnDef<CoachingQuestion> {
  return {
    id: 'acceptanceRate',
    header: 'Accept %',
    accessorKey: 'acceptanceRate',
    size: 90,
    cell: ({ getValue }) => {
      const value = getValue() as number | null
      return value != null ? (
        <span className="text-muted-foreground">{value.toFixed(1)}%</span>
      ) : (
        <span className="text-muted-foreground">-</span>
      )
    },
  }
}

function createSourceColumn(): ColumnDef<CoachingQuestion> {
  return {
    id: 'source',
    header: 'Source',
    accessorKey: 'source',
    size: 120,
    cell: ({ row }) => {
      const allSources = row.original.allSources
      if (allSources?.length) {
        return (
          <div className="flex items-center gap-1">
            {allSources.map((s) => (
              <SourceIconCell
                key={s.id}
                icon={s.sourceIcon}
                name={s.source}
                compact
              />
            ))}
          </div>
        )
      }

      return (
        <SourceIconCell
          icon={row.original.sourceIcon}
          name={row.original.source}
        />
      )
    },
  }
}

function createStudyPlansColumn(): ColumnDef<CoachingQuestion> {
  return {
    id: 'studyPlans',
    header: 'Study Plans',
    accessorKey: 'studyPlans',
    size: 180,
    sortingFn: (rowA, rowB) => {
      const countA = rowA.original.studyPlans?.length ?? 0
      const countB = rowB.original.studyPlans?.length ?? 0
      return countA - countB
    },
    cell: ({ getValue }) => {
      const studyPlans = (getValue() as StudyPlanSummary[] | null) || []
      if (studyPlans.length === 0) {
        return <span className="text-muted-foreground/50 text-xs">—</span>
      }
      return (
        <div
          className="flex flex-wrap gap-1"
          title={studyPlans.map((sp) => sp.name).join(', ')}
        >
          {studyPlans.slice(0, 2).map((sp) => (
            <span
              key={sp.id}
              className="flex items-center gap-1 rounded bg-violet-500/20 px-1.5 py-0.5 text-[10px] text-violet-400"
            >
              {sp.sourceIcon && (
                <AuthoredIcon
                  icon={sp.sourceIcon}
                  size="sm"
                  className="text-muted-foreground shrink-0 rounded"
                  fallback={Library}
                />
              )}
              <span className="max-w-[80px] truncate">{sp.name}</span>
            </span>
          ))}
          {studyPlans.length > 2 && (
            <span className="text-muted-foreground text-xs">
              +{studyPlans.length - 2}
            </span>
          )}
        </div>
      )
    },
  }
}

function createEditorialColumn(): ColumnDef<CoachingQuestion> {
  return {
    id: 'editorialUrl',
    header: 'Editorial',
    accessorKey: 'editorialUrl',
    size: 80,
    enableSorting: false,
    cell: ({ getValue }) => {
      const url = getValue() as string | null
      return url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center justify-center text-purple-500 hover:text-purple-400"
          title="View editorial"
        >
          <BookOpen size={14} />
        </a>
      ) : null
    },
  }
}
